/**
 * PrefIcon — bibliothèque d'icônes SVG pour le modal "Préférences du jour"
 *
 * Remplace les emojis (👕 🥂 etc.) par des icônes ligne premium MyTenue.
 * Gender-aware quand pertinent (Hauts F=blouse / M=t-shirt, etc.).
 *
 * Props :
 *   kind   : 'style' | 'occasion' | 'category'
 *   value  : valeur de l'option (ex: 'chic', 'soirée', 'Hauts')
 *   gender : 'F' | 'M' | 'N' (défaut 'N' = neutre)
 *   size, color, accent
 *
 * v1.12.217
 */

import React from 'react';
import Svg, { Path, Circle, Line, Rect, Polyline, G } from 'react-native-svg';

const STROKE = 1.5;

// ─── helpers ─────────────────────────────────────────────────────────────
const Wrap = ({ size, children }) => (
  <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    {children}
  </Svg>
);

// ─── CATEGORIES ──────────────────────────────────────────────────────────
function CategoryIcon({ value, gender, size, color, accent }) {
  switch (value) {
    case 'Hauts':
      // F = blouse cintrée / M = t-shirt
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M10 8 L13 5 L19 5 L22 8 L26 11 L23 14 L22 13 L22 26 L10 26 L10 13 L9 14 L6 11 Z"
                  stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Path d="M13 5 Q16 8 19 5" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M11 7 L13 5 L19 5 L21 7 L26 10 L23 14 L22 13 L22 27 L10 27 L10 13 L9 14 L6 10 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Path d="M13 5 L16 8 L19 5" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
        </Wrap>
      );

    case 'Bas':
      // F = jupe trapèze / M = pantalon
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M10 6 L22 6 L26 27 L6 27 Z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Line x1="10" y1="9" x2="22" y2="9" stroke={accent} strokeWidth="1.2" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M10 6 L22 6 L23 27 L17 27 L16 12 L15 27 L9 27 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Line x1="10" y1="9" x2="22" y2="9" stroke={accent} strokeWidth="1.2" />
        </Wrap>
      );

    case 'Robes':
      // toujours F
      return (
        <Wrap size={size}>
          <Path d="M11 6 L13 4 L19 4 L21 6 L23 11 L26 27 L6 27 L9 11 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Path d="M13 4 Q16 7 19 4" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          <Line x1="13" y1="14" x2="19" y2="14" stroke={accent} strokeWidth="1.2" />
        </Wrap>
      );

    case 'Vestes':
      // F = veste cintrée / M = blazer carré
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M11 7 L13 5 L19 5 L21 7 L25 11 L23 14 L22 14 L22 27 L18 27 L16 13 L14 27 L10 27 L10 14 L9 14 L7 11 Z"
                  stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Line x1="16" y1="13" x2="16" y2="27" stroke={accent} strokeWidth="1.2" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M11 7 L13 5 L19 5 L21 7 L25 11 L23 14 L22 14 L22 27 L10 27 L10 14 L9 14 L7 11 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Path d="M13 5 L16 11 L19 5" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinejoin="round" />
          <Line x1="16" y1="11" x2="16" y2="27" stroke={accent} strokeWidth="1.2" />
        </Wrap>
      );

    case 'Manteaux':
      // F = manteau long ceinturé / M = trench classique
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M10 7 L13 5 L19 5 L22 7 L26 11 L24 14 L23 14 L23 28 L9 28 L9 14 L8 14 L6 11 Z"
                  stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Line x1="16" y1="5" x2="16" y2="28" stroke={accent} strokeWidth="1.3" />
            <Rect x="13" y="17" width="6" height="2" fill={accent} />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M10 7 L13 5 L19 5 L22 7 L26 11 L24 14 L23 14 L23 28 L9 28 L9 14 L8 14 L6 11 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Line x1="13" y1="5" x2="13" y2="28" stroke={accent} strokeWidth="1.2" />
          <Line x1="19" y1="5" x2="19" y2="28" stroke={accent} strokeWidth="1.2" />
          <Circle cx="13" cy="11" r="0.8" fill={color} />
          <Circle cx="13" cy="17" r="0.8" fill={color} />
          <Circle cx="13" cy="23" r="0.8" fill={color} />
        </Wrap>
      );

    case 'Chaussures':
      // F = escarpin / M = oxford
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M5 22 Q5 18 9 17 L20 14 Q26 13 27 18 Q27 21 24 22 Z"
                  stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Line x1="20" y1="14" x2="22" y2="22" stroke={accent} strokeWidth="1.4" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M5 21 L5 17 Q5 15 8 15 L20 15 Q27 15 27 19 L27 21 Q27 23 24 23 L7 23 Q5 23 5 21 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Line x1="11" y1="15" x2="11" y2="19" stroke={accent} strokeWidth="1.2" />
          <Line x1="14" y1="15" x2="14" y2="19" stroke={accent} strokeWidth="1.2" />
          <Line x1="17" y1="15" x2="17" y2="19" stroke={accent} strokeWidth="1.2" />
        </Wrap>
      );

    case 'Sacs':
      // F = sac à main avec anse arrondie / M = sacoche bandoulière
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M10 12 Q10 6 16 6 Q22 6 22 12" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
            <Rect x="6" y="12" width="20" height="14" rx="2" stroke={color} strokeWidth={STROKE} fill="none" />
            <Line x1="14" y1="17" x2="18" y2="17" stroke={color} strokeWidth="1.2" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M9 11 L23 11 L25 13 L25 25 L7 25 L7 13 Z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Path d="M11 11 L11 7 L21 7 L21 11" stroke={accent} strokeWidth={STROKE} fill="none" />
          <Line x1="7" y1="16" x2="25" y2="16" stroke={color} strokeWidth="1.1" />
        </Wrap>
      );

    case 'Bijoux':
      // toujours F (collier + diamant)
      return (
        <Wrap size={size}>
          <Path d="M7 9 Q16 16 25 9" stroke={color} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          <Path d="M13 16 L16 22 L19 16 L17 14 L15 14 Z" fill={accent} stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
        </Wrap>
      );

    case 'Accessoires':
      // F = foulard noué / M = cravate
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M8 9 Q16 13 24 9 L23 14 Q16 18 9 14 Z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
            <Path d="M14 14 L16 19 L18 14 L17 13 L15 13 Z" fill={accent} stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
            <Path d="M14 19 L13 25 M18 19 L19 25" stroke={color} strokeWidth="1.2" />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M14 6 L18 6 L19 9 L20 12 L17 22 L15 22 L12 12 L13 9 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Path d="M14 6 L16 9 L18 6" stroke={accent} strokeWidth={STROKE} fill="none" strokeLinejoin="round" />
          <Path d="M15 22 L14 26 L18 26 L17 22" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
        </Wrap>
      );

    default:
      return null;
  }
}

// ─── STYLES ──────────────────────────────────────────────────────────────
function StyleIcon({ value, gender, size, color, accent }) {
  switch (value) {
    case 'chic':
      // étoile à 4 branches stylée
      return (
        <Wrap size={size}>
          <Path d="M16 5 L18 14 L27 16 L18 18 L16 27 L14 18 L5 16 L14 14 Z"
                stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.25" strokeLinejoin="round" />
          <Circle cx="16" cy="16" r="1.2" fill={accent} />
        </Wrap>
      );
    case 'casual':
      return (
        <Wrap size={size}>
          <Path d="M11 8 L13 6 L19 6 L21 8 L25 11 L23 14 L22 13 L22 25 L10 25 L10 13 L9 14 L7 11 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
        </Wrap>
      );
    case 'romantique':
      // cœur élégant
      return (
        <Wrap size={size}>
          <Path d="M16 25 C9 20 5 15 5 11 C5 8 7 6 10 6 C12 6 14 7 16 9 C18 7 20 6 22 6 C25 6 27 8 27 11 C27 15 23 20 16 25 Z"
                stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.18" strokeLinejoin="round" />
        </Wrap>
      );
    case 'bohème':
      // brin / feuille
      return (
        <Wrap size={size}>
          <Path d="M16 5 Q22 12 16 27 Q10 12 16 5 Z" stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.18" strokeLinejoin="round" />
          <Line x1="16" y1="9" x2="16" y2="25" stroke={color} strokeWidth="1.1" />
          <Path d="M16 12 L13 14 M16 12 L19 14 M16 16 L12 18 M16 16 L20 18 M16 20 L13 22 M16 20 L19 22" stroke={color} strokeWidth="1.1" />
        </Wrap>
      );
    case 'sport':
      // chronomètre
      return (
        <Wrap size={size}>
          <Circle cx="16" cy="18" r="9" stroke={color} strokeWidth={STROKE} fill="none" />
          <Line x1="13" y1="6" x2="19" y2="6" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
          <Line x1="16" y1="6" x2="16" y2="9" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
          <Line x1="16" y1="18" x2="20" y2="14" stroke={accent} strokeWidth="1.6" strokeLinecap="round" />
          <Circle cx="16" cy="18" r="1" fill={accent} />
        </Wrap>
      );
    case 'streetwear':
      // casquette
      return (
        <Wrap size={size}>
          <Path d="M5 19 Q5 11 16 11 Q27 11 27 19 L23 19 Q23 14 16 14 Q9 14 9 19 Z"
                stroke={color} strokeWidth={STROKE} strokeLinejoin="round" fill="none" />
          <Line x1="5" y1="19" x2="20" y2="19" stroke={accent} strokeWidth="1.5" />
        </Wrap>
      );
    case 'minimaliste':
      // cercle parfait minimal + ligne
      return (
        <Wrap size={size}>
          <Circle cx="16" cy="16" r="9" stroke={color} strokeWidth={STROKE} fill="none" />
          <Line x1="11" y1="16" x2="21" y2="16" stroke={accent} strokeWidth="1.4" strokeLinecap="round" />
        </Wrap>
      );
    case 'workwear':
      // mallette
      return (
        <Wrap size={size}>
          <Rect x="6" y="11" width="20" height="14" rx="2" stroke={color} strokeWidth={STROKE} fill="none" />
          <Path d="M12 11 L12 8 L20 8 L20 11" stroke={color} strokeWidth={STROKE} fill="none" strokeLinejoin="round" />
          <Line x1="6" y1="16" x2="26" y2="16" stroke={accent} strokeWidth="1.3" />
        </Wrap>
      );
    default:
      return null;
  }
}

// ─── OCCASIONS ───────────────────────────────────────────────────────────
function OccasionIcon({ value, gender, size, color, accent }) {
  switch (value) {
    case 'bureau':
      // mallette
      return (
        <Wrap size={size}>
          <Rect x="6" y="11" width="20" height="14" rx="2" stroke={color} strokeWidth={STROKE} fill="none" />
          <Path d="M12 11 L12 8 L20 8 L20 11" stroke={color} strokeWidth={STROKE} fill="none" strokeLinejoin="round" />
          <Line x1="6" y1="16" x2="26" y2="16" stroke={accent} strokeWidth="1.3" />
        </Wrap>
      );
    case 'soirée':
      // coupe à champagne
      return (
        <Wrap size={size}>
          <Path d="M9 6 L23 6 Q22 14 16 16 Q10 14 9 6 Z" stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.2" strokeLinejoin="round" />
          <Line x1="16" y1="16" x2="16" y2="24" stroke={color} strokeWidth={STROKE} />
          <Line x1="11" y1="26" x2="21" y2="26" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
          <Circle cx="13" cy="9" r="0.7" fill={accent} />
          <Circle cx="18" cy="11" r="0.7" fill={accent} />
        </Wrap>
      );
    case 'weekend':
      // tasse de café
      return (
        <Wrap size={size}>
          <Path d="M7 11 L7 22 Q7 25 10 25 L20 25 Q23 25 23 22 L23 11 Z" stroke={color} strokeWidth={STROKE} fill="none" strokeLinejoin="round" />
          <Path d="M23 13 Q27 13 27 17 Q27 21 23 21" stroke={color} strokeWidth={STROKE} fill="none" />
          <Path d="M10 6 Q11 8 10 10 M14 6 Q15 8 14 10 M18 6 Q19 8 18 10" stroke={accent} strokeWidth="1.2" fill="none" />
        </Wrap>
      );
    case 'date':
      // cœur double
      return (
        <Wrap size={size}>
          <Path d="M16 26 C9 21 5 16 5 12 C5 9 7 7 10 7 C12 7 14 8 16 10 C18 8 20 7 22 7 C25 7 27 9 27 12 C27 16 23 21 16 26 Z"
                stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.25" strokeLinejoin="round" />
        </Wrap>
      );
    case 'sport':
      // haltère
      return (
        <Wrap size={size}>
          <Rect x="3" y="13" width="3" height="6" rx="1" fill={color} />
          <Rect x="6" y="11" width="3" height="10" rx="1" fill={color} />
          <Rect x="9" y="15" width="14" height="2" fill={accent} />
          <Rect x="23" y="11" width="3" height="10" rx="1" fill={color} />
          <Rect x="26" y="13" width="3" height="6" rx="1" fill={color} />
        </Wrap>
      );
    case 'vacances':
      // palmier + soleil
      return (
        <Wrap size={size}>
          <Circle cx="22" cy="9" r="3" fill={accent} />
          <Path d="M14 26 L14 14" stroke={color} strokeWidth={STROKE} />
          <Path d="M14 14 Q9 11 6 14 M14 14 Q19 11 22 14 M14 14 Q11 9 8 8 M14 14 Q17 9 20 8" stroke={color} strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          <Line x1="6" y1="26" x2="22" y2="26" stroke={color} strokeWidth="1.2" />
        </Wrap>
      );
    case 'cérémonie':
      // M = nœud papillon / F = voile
      if (gender === 'F') {
        return (
          <Wrap size={size}>
            <Path d="M16 5 Q9 9 9 18 Q9 26 16 27 Q23 26 23 18 Q23 9 16 5 Z"
                  stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.18" strokeLinejoin="round" />
            <Path d="M16 5 L13 11 M16 5 L19 11" stroke={color} strokeWidth="1.2" />
            <Circle cx="16" cy="14" r="1.2" fill={accent} />
          </Wrap>
        );
      }
      return (
        <Wrap size={size}>
          <Path d="M5 13 L13 17 L13 21 L5 25 Z" stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.2" strokeLinejoin="round" />
          <Path d="M27 13 L19 17 L19 21 L27 25 Z" stroke={color} strokeWidth={STROKE} fill={accent} fillOpacity="0.2" strokeLinejoin="round" />
          <Rect x="13" y="16" width="6" height="6" rx="1" stroke={color} strokeWidth={STROKE} fill="none" />
        </Wrap>
      );
    case 'quotidien':
      // soleil simple
      return (
        <Wrap size={size}>
          <Circle cx="16" cy="16" r="5" fill={accent} />
          {[0,45,90,135,180,225,270,315].map(a => {
            const rad = (a * Math.PI) / 180;
            const x1 = 16 + Math.cos(rad) * 8;
            const y1 = 16 + Math.sin(rad) * 8;
            const x2 = 16 + Math.cos(rad) * 11;
            const y2 = 16 + Math.sin(rad) * 11;
            return <Line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.4" strokeLinecap="round" />;
          })}
        </Wrap>
      );
    default:
      return null;
  }
}

// ─── EXPORT ──────────────────────────────────────────────────────────────
export default function PrefIcon({ kind, value, gender = 'N', size = 22, color = '#1A1A1A', accent = '#D4AF72' }) {
  const props = { value, gender, size, color, accent };
  if (kind === 'category') return <CategoryIcon {...props} />;
  if (kind === 'style')    return <StyleIcon {...props} />;
  if (kind === 'occasion') return <OccasionIcon {...props} />;
  return null;
}
